import * as ng from 'angular';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { Conditional, mergeParser } from './merge-parser';
import { propertyParser } from '../../shared';
import { mockEmailFieldValues } from './type/email/email-test-dialog.controller';

const mockQrUrl = require('./asset/sample-qrcode.png');

export class EditorDocument {
   constructor(public doc:Document, public body:HTMLElement) { }

   get html():string {
      // Wrapping the given html in an '#expEditor' div as there is
      // editor specific 'reset' styles being applied to make the
      // editor match the actual site as much as possible.
      return `<div id="expEditor">${this.body.innerHTML}</div>`;
   }

   applyEditableDefaults(settings:any, language:string) {
      $(this.doc).find('[data-experience-text]').each(function(i, e) {
         // get property path of text setting
         // e.g. prop = "$ctrl.settings.section.minAge[$ctrl.language]"
         let propPath = $(e).attr('data-experience-text');

         // stripping $ctrl. from path, needed for propertyParser call below
         propPath = propPath.replace(/\$ctrl./g, '');

         // default text comes from the element content
         let def = $(e).html().trim();

         let prop = propertyParser({settings: settings}, propPath, {language: language});

         let val = prop.get();
         // set default when property is null or undefined, preserve empty strings
         if (ng.isUndefined(val) || val == null) {
            prop.set(def);
         }
      });
   }
}

ng.module('e360')
   .factory('editorCodeParser', editorCodeParserFactory);

editorCodeParserFactory.$inject = ['$q', '$http'];
function editorCodeParserFactory($q:ng.IQService, $http:ng.IHttpService) {
   return editorCodeParser;

   function editorCodeParser(html:string, readonly?:boolean, baseUri?:string):ng.IPromise<EditorDocument> {
      html = replaceDetaults(html);
      html = mergeParser(formatMergeTags, formatIfBlocks, html);

      if (baseUri) {
         html = absoluteImageSrc(html, baseUri);
      }

      var parser = new DOMParser();
      var doc = parser.parseFromString(html, "text/html");

      formatLinks(doc);
      formatEditableImages(doc);
      formatMockMergeTags(doc);

      if (readonly) {
         $(doc).find('[data-experience-text]').each(function(i, e) {
            // get property path of text setting
            let prop = $(e).attr('data-experience-text');

            // remove editable-text directive in read-only mode
            $(e).attr('data-experience-text', null);

            // set binding to property path allowing for html content
            $(e).attr('ng-bind-html', prop + '|unsafe');
         });
      }

      return fixDom(doc, baseUri);
   }

   function fixDom(doc:Document, baseUri?:string) {
      let body = doc.body;
      if (!body) {
         body = doc.documentElement;
      }

      // put all style block at the end of the body
      let styles = doc.getElementsByTagName('style');
      _.each(styles, function(style) {
         style.innerHTML = parseStyle(style.innerHTML);
         body.appendChild(style);
      });

      // get all external css URLs
      let links = doc.getElementsByTagName('link');
      let cssUrls = _.chain(links)
         .map(link => $(link).attr('href'))
         .filter(url => url && url.indexOf('.css') > -1)
         .value();

      // fetch external css, add style blocks at start of body
      return $q.all(_.map(cssUrls, function(url) {
         return $http.get<string>(url).then(function(result) {
            let css = result.data as string;

            if (baseUri) {
               css = css.replace(/url\(/g, 'url(' + baseUri);
            }

            let style = doc.createElement('style');
            style.innerHTML = parseStyle(css);
            body.insertBefore(style, body.firstChild);
         });
      // resolve with result of modified body markup
      })).then(function() {
         return new EditorDocument(doc, body);
      });
   }
}

function replaceDetaults(html:string) {
   // emails are the only thing that use this system of defaults
   let matches = html.match(/\s*<!--\/\/e360defaults([\s\S]*?)-->\s*/i);
   if (!matches) {
      return html;
   }

   _.each(matches[1].split('\n'), function(line) {
      let parts = line.trim().split(/\s(.+)?/);
      if (parts[0].substring(0,1) !== '@') {
         return html;
      }

      // get the key/value from the defaults
      let key = parts[0].substring(1);
      let value = parts[1] || '';

      // replace values in template for each detault, this becomes
      // the element text for experience-text directive which applies
      // the default value if model has no value
      html = html.replace(`*|${key}|*`, value);
   });

   return html;
}

function formatMergeTags(tag:string) {
   // mandrill doesn't like "." so we use "_" instead
   tag = tag.replace('_', '.');

   if (tag.indexOf('extra.') > -1 || tag.indexOf('uploads.') > -1 || tag.indexOf('section.') > -1) {
      tag = tag + '[$ctrl.language]';
      return `{{$ctrl.settings.${tag}? $ctrl.settings.${tag} : '&nbsp;'}}`;
   } else if (tag[0] == tag[0].toLowerCase()) {
      // assume lowercase first char means settings like "backgroundColor"
      return `{{$ctrl.settings.${tag}}}`;
   } else {
      // otherwise assume merge tag inside editable text
      return `*|${tag}|*`;
   }
}

function formatIfBlocks(cond:Conditional) {
   const tooltips = {
      'PassbookURL': 'uib-tooltip="This will only appear in emails if a passbook experience has been configured." tooltip-append-to-body="true"',
      'QRImageURL': 'uib-tooltip="This can be hidden by selecting hide QR image under appearance settings."',
      'QRCodeURL': 'uib-tooltip="This will only appear in emails if a QR page experience has been configured." tooltip-append-to-body="true"',
      'campaign_twitterLink': 'uib-tooltip="This will only appear in emails if you have a link to your Twitter profile configured in campaign settings."',
      'campaign_facebookLink': 'uib-tooltip="This will only appear in emails if you have a link to your Facebook page configured in campaign settings."',
      'campaign_instagramLink': 'uib-tooltip="This will only appear in emails if you have a link to your Instagram page configured in campaign settings."'
   };

   const viewOptions = {
      'MinAge': '$ctrl.campaign.ageRequirementType',
      'EventID': '$ctrl.editorCtrl.model.viewOptions.showEventBox'
   };

   const block = cond.blocks[0];
   const mergeVar = block.exp;
   let body = cond.blocks.join(''); //block.toString();

   if (viewOptions[mergeVar]) {
      body = body.replace('<tr', `<tr ng-if="${viewOptions[mergeVar]}"`);
   } else if (mergeVar == 'QRImageURL') {
      body = body.replace('*|QRImageURL|*', mockQrUrl);
      body = `<span class="conditional" ng-if="!$ctrl.settings.hideQrCodeImage" ${tooltips[mergeVar]}>${body}</span>`;
   } else if (tooltips[mergeVar]) {
      if (body.trim().indexOf('<tr') == 0) {
         body = body.replace('<tr', `<tr ${tooltips[mergeVar]}`);
      } else {
         body = `<span class="conditional" ${tooltips[mergeVar]}>${body}</span>`;
      }
   }

   return body;
}

function formatLinks(doc:Document) {
   $(doc).find('a').each(function(i, e) {
      $(e).attr('href', '');
   });
}

function formatEditableImages(doc:Document) {
   $(doc).find('[data-experience-image]').each(function(i, e) {
      let attrs = JSON.parse($(e).attr('data-experience-image'));

      attrs['ng-model'] = '$ctrl.settings.uploads.' + attrs.key + '[$ctrl.language]';

      if (attrs.key === 'headerImage') {
         attrs.default = '$ctrl.editorCtrl.getDefaultImage("' + attrs.key + '")';
      }

      delete attrs.key;

      $(e).replaceWith($('<img-upload></img-upload>').attr(attrs));
   });
}

function formatMockMergeTags(doc:Document) {
   $(doc).find('.email-event-box').each((i, e) => {
      let html = e.outerHTML;

      for (const key in mockEmailFieldValues) {
         html = replaceAll(html, `*|${key}|*`, mockEmailFieldValues[key]);
      }

      $(e).replaceWith(html);
   });
}

/**
 * Formats CSS for use in editor.
 * Prefix each rule (except those that start with '@') with '#expEditor'
 * as a way to namespace the styling.
 */
function parseStyle(style:string) {
   return style.replace(/([^\r\n,{}]+)(,(?=[^}]*{)|\s*{[^{])/gi, function(match) {
      var str = match.trim();

      // leave rules like '@viewport' or '@media' alone
      if (str[0] == '@') {
         return match;
      }

      // remove body/html tag names from rules... because reasons?
      // probably to use #expEditor as the "root" element of stylesheet
      str = str.replace('html', '');
      str = str.replace('body', '');

      return '#expEditor ' + str;
   });
}

function absoluteImageSrc(html:string, baseUri:string) {
   return html.replace(/ src="/g, ' src="' + baseUri);
}

function replaceAll(str, find, replace) {
   const pattern = find.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
   return str.replace(new RegExp(pattern, 'g'), replace);
}
